'use client';

import { type FC } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SearchResultPageZeroResultsFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchResultPageZeroResultsFragmentFragmentDoc';

import { ZeroResultsNoticeMessage } from '../ZeroResultsNoticeMessage/ZeroResultsNoticeMessage';
import { TopCategories } from '../TopCategories/TopCategories';
import { NeedHelpSection } from '../NeedHelpSection/NeedHelpSection';
import { SearchTipsSection } from '../SearchTipsSection/SearchTipsSection';
import { SearchFeedback } from '../SearchFeedback/SearchFeedback.client';
import { useZeroResultsTracking } from './useZeroResultsTracking';
import { SearchKimRating } from '../SearchKimRating/SearchKimRating';
import { bypassZeroResultsPageMessages } from './messages';

/* GraphQL */ `
  fragment SearchResultPageZeroResultsFragment on EmptySearchResult {
    topCategories {
      ...TopCategoriesFragment
    }
    ...SearchKimRatingFragment
  }
`;

export type SearchResultPageZeroResultsProps = {
  maskedResult: FragmentType<typeof SearchResultPageZeroResultsFragmentFragmentDoc>;
  referrer?: string;
};

/**
 * Search result page if no results are available
 * Reasons for that could be:
 * - search query was on blacklist
 * - no normal results, and QueryReduction and DeepQuery both failed
 * */
export const SearchResultPageZeroResults: FC<SearchResultPageZeroResultsProps> = ({
  maskedResult,
  referrer,
}) => {
  const result = unmask<typeof SearchResultPageZeroResultsFragmentFragmentDoc>(maskedResult);
  const { formatMessage } = useIntl();

  const { searchResultUserFeedback } = useConfig();

  useZeroResultsTracking();

  return (
    <div className="flex flex-col gap-1">
      <div>
        <ZeroResultsNoticeMessage />
      </div>
      <div className="mt-1">
        <SearchTipsSection />
      </div>
      <div className="bg-color-grey-light mt-1 p-2">
        <BypassBlock buttonText={formatMessage(bypassZeroResultsPageMessages.topCategories)}>
          <TopCategories maskedTopCategories={result.topCategories} />
        </BypassBlock>
      </div>
      {searchResultUserFeedback.enabled && (
        <div className="mt-1">
          <SearchFeedback />
        </div>
      )}
      <div className="mt-1">
        <NeedHelpSection />
      </div>
      <ErrorBoundary>
        <SearchKimRating
          maskedData={result}
          searchPageType="SERP"
          templateParams={{ x17: referrer ?? 'x' }}
        />
      </ErrorBoundary>
    </div>
  );
};
